// Admin roster page. Lists every user in the system grouped by their
// `type` field (1=Admin, 2=Verifier, 3=Doctor, 7=Customer/Partner,
// 8=Admin Verifier). Chips at the top show counts per type and act as
// the type filter; a search box filters by name/email/phone across the
// whole roster. Read-only — click a row to copy the user_id; future
// follow-ups could add edit/deactivate inline.
//
// Backed by GET /api/users/listByType, gated to verifiertype === "8".
import React, { Component } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import SideBar from "../../common/SideBar";
import { apiUrl } from "../../../config/config";
import "./UsersByType.css";

const TYPE_ORDER = [2, 8, 3, 6, 7, 1]; // chip display order
const TYPE_LABELS_FALLBACK = {
  1: "Admin",
  2: "Verifier",
  3: "Doctor",
  6: "Hospital",                   // NEW — hospitals stored as type 6,
                                   // linked to their doctors via database_id
  7: "Customer / Partner",
  8: "Admin Verifier",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

// Role / type options exposed in the edit modal. Order is the order
// the dropdown shows them in. The numeric `value` is what gets stored
// on user.type and is what the rest of the app gates permissions on.
const ROLE_OPTIONS = [
  { value: 2, label: "Verifier",            hint: "Reads ECGs, verifies analysis" },
  { value: 8, label: "Admin Verifier",      hint: "Verifier + admin dashboards (telemetry, users)" },
  { value: 3, label: "Doctor",              hint: "Owns patients, signs reports" },
  { value: 6, label: "Hospital",            hint: "Clinic/hospital — doctors are linked via database_id" },
  { value: 7, label: "Customer / Partner",  hint: "Clinic / distributor — WCN customer id applies" },
  { value: 1, label: "Admin",               hint: "System-level admin (legacy)" },
];

// Sub-category within type=7. A Hospital can be linked to a parent
// Partner via parentCustomerId so reporting/invoicing rolls up.
const CATEGORY_OPTIONS = [
  { value: "partner",     label: "Partner",     hint: "Direct customer / distributor / clinic" },
  { value: "hospital",    label: "Hospital",    hint: "End-user hospital — may roll up under a partner" },
  { value: "distributor", label: "Distributor", hint: "Reseller — usually a partner with multiple hospitals" },
];

// Fields admins can change inline. Backend whitelists the same set.
// contactPerson / whatsappNumber / mailContact propagate to every
// device_infos row sharing this customerId, so the device-details page
// reflects the change automatically.
const EDITABLE_FIELDS = [
  { key: "customerId",     label: "Customer ID",     placeholder: "e.g. WCN082", mono: true },
  { key: "name",           label: "Display name",    placeholder: "Customer / partner name" },
  { key: "customerName",   label: "Customer name",   placeholder: "Long-form / legal name (optional)" },
  { key: "email",          label: "Email",           placeholder: "email@example.com", type: "email" },
  { key: "phone",          label: "Phone",           placeholder: "10-digit number" },
  { key: "contactPerson",  label: "Account Coordinator", placeholder: "Person who liaises with us on this account" },
  { key: "whatsappNumber", label: "WhatsApp number", placeholder: "10-digit number for WhatsApp reports" },
  { key: "mailContact",    label: "Contact email",   placeholder: "contact@example.com", type: "email" },
  // Free-text customisation notes for this customer — also shown (and
  // editable) inside the Add Billing modal on Verifier Home.
  { key: "customerComments", label: "Customer comments (customisation)", placeholder: "Report format, delivery instructions, billing notes…", multiline: true },
  // Signing-doctor handle — matches uncontrolledValues.__signatureDoctorId
  // on the patient document. Only meaningful for type 3 (Doctor) or
  // type 9 (Signing Doctor) accounts; harmless on others.
  { key: "signatureDoctorId", label: "Signing handle", placeholder: "e.g. prajjal", mono: true },
  // database_id is the join key between Hospital (type 6) and Doctor
  // (type 3). When a hospital row's database_id matches a doctor's
  // database_id, that doctor is listed under the hospital. A hospital
  // with NO database_id silently matches every doctor — which is the
  // "all doctors showing under one hospital" bug. Edit this field to
  // explicitly point at the doctor record this hospital should own.
  { key: "database_id",       label: "Linked DB ID",   placeholder: "ObjectId of the doctor record this hospital owns", mono: true },
];

export default class UsersByType extends Component {
  state = {
    activeType: 7,           // default to Customer / Partner — that's the main admin job
    activeCategory: null,    // null = any; only meaningful when activeType === 7
    qInput: "",
    qApplied: "",
    onlyActive: false,
    users: [],
    typeCounts: {},
    typeLabels: TYPE_LABELS_FALLBACK,
    total: 0,
    offset: 0,
    limit: 100,
    loading: false,
    error: "",

    // Edit modal
    editing: null,        // a user object being edited (null = closed)
    editDraft: {},        // working values
    saving: false,
    saveError: "",

    // List of {customerId, name, accountCategory} for the parent-partner
    // dropdown when editing a Hospital. Loaded once on demand.
    partnersForParent: [],

    // ── Stats modal ──
    statsOpen:    false,
    statsPeriod:  "today",            // today | week | month | custom
    statsFrom:    "",                  // YYYY-MM-DD (custom range only)
    statsTo:      "",                  // YYYY-MM-DD (custom range only)
    statsLoading: false,
    statsError:   "",
    statsRows:    [],                  // [{customerId, customerName, registered, ...}]
  };

  componentDidMount() {
    // Both Admin (type 1) and Admin Verifier (type 8) can see this page.
    const t = localStorage.getItem("verifiertype");
    if (t !== "8" && t !== "1") {
      this.setState({ error: "This page is for admins only." });
      return;
    }
    this.load();
  }

  buildParams = () => {
    const { activeType, activeCategory, qApplied, onlyActive, offset, limit } = this.state;
    const p = new URLSearchParams();
    if (activeType != null) p.set("type", String(activeType));
    if (activeType === 7 && activeCategory) p.set("category", activeCategory);
    if (qApplied) p.set("q", qApplied);
    if (onlyActive) p.set("active", "true");
    p.set("offset", String(offset));
    p.set("limit", String(limit));
    return p.toString();
  };

  load = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get(`${apiUrl}api/users/listByType?${this.buildParams()}`);
      this.setState({
        users: res.data.users || [],
        total: res.data.total || 0,
        typeCounts: res.data.typeCounts || {},
        typeLabels: res.data.typeLabels || TYPE_LABELS_FALLBACK,
        loading: false,
      });
    } catch (err) {
      this.setState({
        loading: false,
        error: (err.response && err.response.data && err.response.data.error) || err.message,
      });
    }
  };

  pickType = (t) => {
    // Reset sub-category whenever the top-level type changes
    this.setState({ activeType: t, activeCategory: null, offset: 0 }, this.load);
  };
  pickCategory = (c) => {
    this.setState({ activeCategory: c, offset: 0 }, this.load);
  };

  applySearch = () => {
    this.setState({ qApplied: this.state.qInput.trim(), offset: 0 }, this.load);
  };

  // Debounced live search — applies after the user stops typing for 280ms.
  // Avoids hammering the backend with one request per keystroke.
  searchDebounceTimer = null;
  onSearchInput = (val) => {
    this.setState({ qInput: val });
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => this.applySearch(), 280);
  };
  clearSearch = () => {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.setState({ qInput: "", qApplied: "", offset: 0 }, this.load);
  };

  // ────────────────────────────────────────────────────────────────
  // Excel Import / Export
  // ────────────────────────────────────────────────────────────────
  // Match key for import: `_id` (Mongo ObjectId). Rows whose _id
  // doesn't exist in the currently loaded users list are skipped —
  // never created. Summary toast shows updated + skipped + failed.
  // ────────────────────────────────────────────────────────────────
  // Aligned with EDITABLE_FIELDS at the top of this file (line 73) so
  // import/export and the edit modal stay in sync. Adding a new editable
  // field to that list? Add it here too. `type` and `isActive` are not
  // in EDITABLE_FIELDS because they have their own controls (select +
  // checkbox), but they're real user-record fields and worth exporting.
  USER_EXPORT_FIELDS = [
    "_id",                 // Mongo ObjectId — match key, read-only on import
    "customerId",          // e.g. WCN082 — partner / hospital identifier
    "name",                // display name
    "customerName",        // long-form / legal name
    "email",
    "phone",
    "type",                // 1=Admin, 2=Verifier, 3=Doctor, 7=Customer, 8=AdminVerifier, 9=Signing Dr
    "accountCategory",     // type-7 sub-category: partner | hospital | distributor
    "parentCustomerId",    // hospital → parent partner link
    "parentCustomerName",  // resolved parent partner display name (read-only — change parentCustomerId to remap)
    "contactPerson",       // Account Coordinator
    "whatsappNumber",      // 10-digit for WhatsApp reports
    "mailContact",         // contact email
    "customerComments",    // customisation notes (also shown in Add Billing)
    "signatureDoctorId",   // signing handle (type 9 only)
    "database_id",         // hospital → linked doctor record
    "isActive",            // account active flag (boolean)
  ];
  // Fields the import logic must NEVER overwrite. Only the Mongo _id
  // is truly untouchable. `parentCustomerName` is editable — when
  // changed in Excel, the import logic resolves it to a partner's
  // customerId and updates parentCustomerId accordingly (keeping the
  // data normalized).
  USER_IMMUTABLE_FIELDS = ["_id"];

  fileInputRef = React.createRef();

  handleExportUsers = async () => {
    try {
      const rows = this.state.users || [];
      if (!rows.length) {
        alert("No users to export");
        return;
      }

      // The /listByType endpoint returns a SUMMARY projection only —
      // _id, name, email, phone, type, customerId, customerName, isActive.
      // Fields like contactPerson, accountCategory, whatsappNumber,
      // mailContact, parentCustomerId, parentCustomerName, signatureDoctorId
      // and database_id are stored in the DB but NOT returned by the list
      // route (verified via console inspection — 2026-06-23).
      //
      // To get the full record we fetch each user individually via
      // GET /api/users/<_id> (same route the edit modal uses to populate
      // its form). Batched in groups of 10 to keep the backend happy.
      this.setState({ exportBusy: true });
      const BATCH = 10;
      const fullById = {};
      for (let i = 0; i < rows.length; i += BATCH) {
        const slice = rows.slice(i, i + BATCH);
        const results = await Promise.allSettled(
          slice.map((u) => axios.get(`${apiUrl}api/users/${u._id}`))
        );
        results.forEach((r, j) => {
          if (r.status === "fulfilled") {
            const u = r.value && r.value.data && r.value.data.user
                  ? r.value.data.user        // {user: {...}}
                  : r.value && r.value.data; // raw user
            if (u && u._id) fullById[String(u._id)] = u;
          }
        });
      }

      // Build export rows from the full records, falling back to the
      // summary row if a per-user fetch failed (rare, but defensive).
      const data = rows.map((summary) => {
        const u = fullById[String(summary._id)] || summary;
        const row = {};
        this.USER_EXPORT_FIELDS.forEach((k) => {
          row[k] = u[k] != null ? u[k] : "";
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(data, { header: this.USER_EXPORT_FIELDS });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      const stamp = new Date().toISOString().slice(0, 10);
      const typeLabel = this.state.activeType != null ? `type${this.state.activeType}_` : "";
      XLSX.writeFile(wb, `users_${typeLabel}${stamp}.xlsx`);
    } catch (err) {
      console.error("[UsersByType] export failed:", err);
      alert("Export failed: " + err.message);
    } finally {
      this.setState({ exportBusy: false });
    }
  };

  handleImportClick = () => {
    if (this.fileInputRef.current) this.fileInputRef.current.click();
  };

  handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;

    this.setState({ importBusy: true });
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rows.length) {
        alert("Imported file is empty");
        this.setState({ importBusy: false });
        return;
      }

      // Build _id → existing user map for O(1) lookup.
      //
      // IMPORTANT: state.users only holds the currently-filtered slice
      // (e.g. activeType=7 means only Customers are in state). If the
      // Excel file contains users of other types, they would all be
      // counted as "skipped (no match)" — not because the _ids are
      // wrong, but because they weren't loaded.
      //
      // The /listByType backend requires a `type` param (returns 400
      // without one), so we can't ask for "all". Instead fire ONE
      // request per known type in parallel and merge. Covers every
      // role the app currently uses: 1=Admin, 2=Verifier, 3=Doctor,
      // 6=Hospital, 7=Customer, 8=Admin Verifier, 9=Signing Doctor.
      const ALL_TYPES = [1, 2, 3, 6, 7, 8, 9];
      let fullUsers = [];
      try {
        const results = await Promise.allSettled(
          ALL_TYPES.map((t) =>
            axios.get(`${apiUrl}api/users/listByType?type=${t}&limit=10000&offset=0`)
          )
        );
        results.forEach((r, i) => {
          if (r.status === "fulfilled" && r.value && r.value.data && Array.isArray(r.value.data.users)) {
            fullUsers = fullUsers.concat(r.value.data.users);
          } else if (r.status === "rejected") {
            console.warn(`[UsersByType] fetch failed for type=${ALL_TYPES[i]}:`,
              r.reason && r.reason.message);
          }
        });
        // De-dup just in case the backend returns the same user for
        // multiple type queries (shouldn't, but defensive).
        const dedup = {};
        fullUsers.forEach((u) => { if (u && u._id) dedup[String(u._id)] = u; });
        fullUsers = Object.values(dedup);

        if (fullUsers.length === 0) {
          // All requests failed → fall back to current state so import
          // is still partially usable.
          fullUsers = this.state.users || [];
          console.warn("[UsersByType] all type fetches failed; falling back to filtered list");
        }
      } catch (e) {
        console.warn("[UsersByType] full user fetch failed, falling back to filtered list:", e.message);
        fullUsers = this.state.users || [];
      }

      const byId = {};
      fullUsers.forEach((u) => {
        if (u._id) byId[String(u._id).trim()] = u;
      });

      // Build partner-name → customerId map for resolving
      // parentCustomerName edits. Only type-7 partners are valid
      // parents; hospitals/distributors are not. Case-insensitive,
      // trimmed match. If multiple partners share a name, the first
      // one wins (rare but possible — admin can clean up duplicates).
      const partnerIdByName = {};
      fullUsers.forEach((u) => {
        if (
          u.type === 7 &&
          u.accountCategory === "partner" &&
          u.name &&
          u.customerId &&
          !partnerIdByName[String(u.name).trim().toLowerCase()]
        ) {
          partnerIdByName[String(u.name).trim().toLowerCase()] = u.customerId;
        }
      });

      let updated = 0;
      const skippedRows = [];   // log of {_id, name} for diagnostic output
      const errors = [];

      // Type-coercion for Excel cells. Excel writes booleans as "TRUE"/
      // "FALSE" strings and numbers as either Number or numeric strings.
      // The backend expects: isActive=boolean, type=number, everything
      // else = string. Without coercion an import would store the string
      // "FALSE" (which is TRUTHY in JS) into the boolean field — exactly
      // the opposite of what the user typed.
      const coerce = (key, raw) => {
        if (key === "isActive") {
          if (typeof raw === "boolean") return raw;
          const s = String(raw).trim().toLowerCase();
          return s === "true" || s === "yes" || s === "1" || s === "y";
        }
        if (key === "type") {
          const n = parseInt(raw, 10);
          return isNaN(n) ? undefined : n;
        }
        return raw;
      };

      for (const r of rows) {
        // Match key comes from the EXISTING row in the table — we look
        // it up via r._id (Excel column) but use existing._id for the
        // URL. This means even if the user edited _id in the Excel file
        // to a non-existing value, the row is just skipped (no match).
        // We NEVER use the user-provided _id to address the API.
        const id = r._id ? String(r._id).trim() : "";
        if (!id || !byId[id]) {
          skippedRows.push({ _id: id || "(blank)", name: r.name || "(no name)" });
          continue;
        }
        const existing = byId[id];

        // Build payload from ONLY the export-fields whitelist.
        // NEVER spread `existing` — it carries server-managed fields
        // like `patients`, `password`, `__v`, `createdAt`, `updatedAt`
        // that strict-mode Mongoose validators reject on PUT.
        // For each whitelisted field: take Excel value if non-empty,
        // else fall back to the existing value so we never silently
        // blank a field the user didn't touch.
        const payload = {};
        this.USER_EXPORT_FIELDS.forEach((k) => {
          if (this.USER_IMMUTABLE_FIELDS.indexOf(k) !== -1) return;
          // parentCustomerName is editable but actually resolves to a
          // parentCustomerId update — handled in the block below, not
          // stored as a direct field on the user.
          if (k === "parentCustomerName") return;
          let v;
          if (r[k] !== "" && r[k] != null) {
            v = coerce(k, r[k]);
          } else if (existing[k] !== undefined && existing[k] !== null) {
            v = existing[k];
          }
          if (v !== undefined) payload[k] = v;
        });

        // If the import row edited parentCustomerName, look it up in
        // the partner map and update parentCustomerId. Only valid type
        // 7 partners (accountCategory === "partner") can be parents.
        // If no match found, parentCustomerId is left as whatever it
        // was (existing or imported value) — never blanked silently.
        if (r.parentCustomerName && r.parentCustomerName !== "") {
          const key = String(r.parentCustomerName).trim().toLowerCase();
          if (partnerIdByName[key]) {
            payload.parentCustomerId = partnerIdByName[key];
          }
        }

        delete payload._id;
        // parentCustomerName is server-derived — don't send it back as
        // a stored field. The backend re-resolves on next fetch from
        // the (possibly updated) parentCustomerId.
        delete payload.parentCustomerName;

        // Diagnostic: log the FIRST row's payload + row so we can verify
        // that all expected fields actually leave the frontend. If the
        // user reports "field X isn't being saved", this is the quickest
        // way to tell whether the issue is in our code or the backend.
        if (updated === 0 && errors.length === 0 && skippedRows.length === 0) {
          console.log("[UsersByType] import sample row from Excel:", r);
          console.log("[UsersByType] import sample payload PUT to backend:", payload);
          console.log("[UsersByType] import sample URL:", `${apiUrl}api/users/${existing._id}`);
        }

        try {
          // URL uses the AUTHORITATIVE existing._id, never r._id.
          await axios.put(`${apiUrl}api/users/${existing._id}`, payload);
          updated++;
        } catch (err) {
          // Capture the backend's actual error body so the user can see
          // WHY the row was rejected, not just the HTTP status code.
          // `axios` puts the server response under err.response.data —
          // shape varies by backend (string, {error}, {message}, etc.)
          const status = err.response && err.response.status;
          const body   = err.response && err.response.data;
          const reason =
            (body && typeof body === "string" && body) ||
            (body && body.error) ||
            (body && body.message) ||
            (body && JSON.stringify(body).slice(0, 200)) ||
            err.message;
          errors.push({
            _id:     existing._id,
            name:    existing.name || "",
            status,
            reason,
            payload,                  // include what we tried to send
          });
        }
      }

      // Reload to reflect persisted changes.
      this.load();

      const parts = [`${updated} updated`];
      if (skippedRows.length) parts.push(`${skippedRows.length} skipped (no match)`);
      if (errors.length)      parts.push(`${errors.length} failed`);
      alert(parts.join(", ") + (skippedRows.length || errors.length ? "\n\nSee browser console (F12) for details on skipped/failed rows." : ""));
      if (skippedRows.length) {
        console.warn(`[UsersByType] ${skippedRows.length} rows skipped (no matching _id):`, skippedRows);
      }
      if (errors.length) {
        console.warn(`[UsersByType] ${errors.length} rows failed to update:`, errors);
      }
    } catch (err) {
      console.error("[UsersByType] import failed:", err);
      alert("Import failed: " + err.message);
    } finally {
      this.setState({ importBusy: false });
    }
  };

  // ── Stats modal ──────────────────────────────────────────────────
  openStats = () => {
    this.setState({ statsOpen: true, statsError: "", statsRows: [] }, this.loadStats);
  };
  closeStats = () => this.setState({ statsOpen: false });

  // Resolve {from, to} as YYYY-MM-DD for the current period
  resolveStatsRange = () => {
    const { statsPeriod, statsFrom, statsTo } = this.state;
    const fmt = (d) => d.toISOString().slice(0, 10);
    const now = new Date();
    if (statsPeriod === "today") {
      const s = new Date(now); s.setHours(0,0,0,0);
      return { from: fmt(s), to: fmt(s) };
    }
    if (statsPeriod === "week") {
      const s = new Date(now); s.setDate(s.getDate() - 6); s.setHours(0,0,0,0);
      return { from: fmt(s), to: fmt(now) };
    }
    if (statsPeriod === "month") {
      const s = new Date(now); s.setDate(s.getDate() - 29); s.setHours(0,0,0,0);
      return { from: fmt(s), to: fmt(now) };
    }
    return { from: statsFrom || fmt(now), to: statsTo || fmt(now) };
  };

  setStatsPeriod = (p) => {
    // Switching period: drop any stale "no patients" error from the prior tab
    this.setState({ statsPeriod: p, statsError: "" },
      () => p !== "custom" && this.loadStats());
  };

  loadStats = async () => {
    const { from, to } = this.resolveStatsRange();
    if (!from || !to) return;
    // Always clear the stale error from a previous tab/export attempt
    this.setState({ statsLoading: true, statsError: "" });
    try {
      const res = await axios.get(
        `${apiUrl}api/users/customerStats?from=${from}&to=${to}`
      );
      this.setState({
        statsRows: res.data.stats || [],
        statsLoading: false,
        statsError: "",
      });
    } catch (err) {
      this.setState({
        statsLoading: false,
        statsError:
          (err.response && err.response.data && err.response.data.error) ||
          err.message ||
          "Failed to load stats",
      });
    }
  };

  // Fetch the period's patient-level detail and download a per-patient
  // CSV grouped by customer. One row per patient, sorted Customer ID
  // ascending → registered_at ascending. SL NO restarts at 1 per
  // customer so an accountant can see "this customer had N patients
  // this period" at a glance.
  //
  // Pass `forCustomerId` to scope the download to a single customer
  // (used by the per-row "Download" button on the stats table).
  exportStatsCsv = async (forCustomerId) => {
    const { from, to } = this.resolveStatsRange();
    if (!from || !to) return;

    this.setState({ statsError: "" });
    try {
      const params = new URLSearchParams({ from, to });
      if (forCustomerId) params.set("customerId", forCustomerId);
      const res = await axios.get(
        `${apiUrl}api/users/customerPatientList?${params.toString()}`
      );
      const patients = res.data.patients || [];
      if (!patients.length) {
        this.setState({ statsError: "No patients in this period — nothing to export." });
        return;
      }

      // Format helpers
      const fmtDateTime = (iso) => {
        if (!iso) return "";
        try {
          return new Date(iso).toISOString().slice(0, 19).replace("T", " ");
        } catch { return ""; }
      };

      // SL NO restarts per customer
      let slNo = 0;
      let lastCustomer = null;

      // Columns the user asked for. Monitoring duration is intentionally
      // LAST. Report-delivered date sits beside report-generated date.
      // Confirmation columns mirror the Report-Shared status page.
      const header = [
        "SL NO",
        "Customer ID", "Customer name", "Category",
        "Patient ID", "Patient name", "Device ID", "Phone", "Hospital",
        "Registered date",
        "Report status",
        "Report generated date",
        "Report delivered date",
        "Confirmation status",            // Yes / No / Hold
        "Confirmed by",                   // person/source who confirmed
        "Confirmation source",            // staff / whatsapp
        "Confirmation date",              // when confirmation flipped
        "Uploaded?", "Report generated?",
        "Data count",
        "Monitoring duration (days)",     // ← last column
      ];

      const rows = patients.map((p) => {
        // Reset SL NO at every new customer
        if (p.customerId !== lastCustomer) {
          slNo = 1;
          lastCustomer = p.customerId;
        } else {
          slNo += 1;
        }
        return [
          slNo,
          p.customerId,
          p.customerName,
          p.accountCategory,
          p.patientId,
          p.patientName,
          p.device_id,
          p.phone,
          p.hospital,
          fmtDateTime(p.created_at),
          p.report_status_label,
          fmtDateTime(p.report_generated_at),
          fmtDateTime(p.report_delivered_at),
          p.confirmation || "",
          p.confirmed_by || "",
          p.confirmation_source || "",
          fmtDateTime(p.confirmation_at),
          p.uploaded ? "Yes" : "No",
          p.report_generated ? "Yes" : "No",
          p.data_count,
          p.exp_device_use_days,
        ];
      });

      const csv = [header, ...rows]
        .map((row) => row.map((v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      // BOM so Excel opens UTF-8 correctly
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = forCustomerId
        ? `patients_${forCustomerId}_${from}_to_${to}.csv`
        : `patients-by-customer_${from}_to_${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      this.setState({
        statsError:
          (err.response && err.response.data && err.response.data.error) ||
          err.message ||
          "Export failed",
      });
    }
  };

  toggleActiveOnly = () => {
    this.setState(
      (s) => ({ onlyActive: !s.onlyActive, offset: 0 }),
      this.load
    );
  };

  changePage = (delta) => {
    const next = Math.max(0, this.state.offset + delta * this.state.limit);
    if (next >= this.state.total && delta > 0) return;
    this.setState({ offset: next }, this.load);
  };

  copyId = (id) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(String(id)).catch(() => {});
  };

  // ── Edit modal ────────────────────────────────────────────────────
  openEdit = (u) => {
    const draft = {};
    for (const f of EDITABLE_FIELDS) draft[f.key] = u[f.key] || "";
    draft.isActive         = u.isActive !== false;
    draft.type             = u.type;
    draft.accountCategory  = u.accountCategory  || "partner";
    draft.parentCustomerId = u.parentCustomerId || "";
    this.setState({ editing: u, editDraft: draft, saveError: "" }, this.ensurePartnersLoaded);

    // The list payload may not include customerComments — fetch the stored
    // value so opening + saving doesn't unintentionally blank it.
    if (!draft.customerComments && (u.name || u.customerId)) {
      axios
        .get(`${apiUrl}api/users/getCustomerComments`, {
          params: { name: u.name || u.customerId },
        })
        .then((res) => {
          const c = (res.data && res.data.comments) || "";
          if (!c) return;
          this.setState((prev) =>
            prev.editing && String(prev.editing._id) === String(u._id)
              ? { editDraft: { ...prev.editDraft, customerComments: c } }
              : null
          );
        })
        .catch(() => {});
    }
  };

  // Load partner-only customer list lazily, the first time a Hospital
  // edit modal opens. Reused across opens.
  ensurePartnersLoaded = async () => {
    if (this.state.partnersForParent.length) return;
    try {
      const res = await axios.get(
        `${apiUrl}api/users/listByType?type=7&category=partner&limit=500`
      );
      this.setState({
        partnersForParent: (res.data.users || []).filter((u) => u.customerId),
      });
    } catch {
      /* silent — parent picker just stays empty */
    }
  };
  closeEdit = () => this.setState({ editing: null, editDraft: {}, saveError: "" });
  setDraft = (key, val) =>
    this.setState((s) => ({ editDraft: { ...s.editDraft, [key]: val } }));

  saveEdit = async (e) => {
    // Defensive: kill any default form submit behaviour
    if (e && e.preventDefault) e.preventDefault();

    /* eslint-disable no-console */
    console.log("[UsersByType] saveEdit FIRED");
    const { editing, editDraft } = this.state;
    console.log("[UsersByType] editing._id =", editing && editing._id);
    console.log("[UsersByType] draft        =", editDraft);

    if (!editing) {
      console.warn("[UsersByType] no `editing` user — bailing");
      return;
    }

    // Coerce type to integer before sending — the <select> gives us a string
    const payload = { ...editDraft };
    if (payload.type !== undefined && payload.type !== "") {
      payload.type = parseInt(payload.type, 10);
    }

    // Soft guardrail: warn before a self-demotion or losing admin role
    const myId = localStorage.getItem("userId") || "";
    if (
      String(editing._id) === String(myId) &&
      editing.type !== payload.type
    ) {
      const ok = window.confirm(
        "You're changing your OWN role. You may lose access to this page after saving. Continue?"
      );
      if (!ok) {
        console.log("[UsersByType] user cancelled self-demotion");
        return;
      }
    }
    if (editing.type === 8 && payload.type !== 8) {
      const ok = window.confirm(
        `Demote "${editing.name}" from Admin Verifier? They'll lose access to Telemetry / Users-by-role / Analysis Dashboard.`
      );
      if (!ok) {
        console.log("[UsersByType] user cancelled admin demotion");
        return;
      }
    }

    console.log("[UsersByType] about to PUT", `${apiUrl}api/users/${editing._id}`, payload);
    /* eslint-enable no-console */

    this.setState({ saving: true, saveError: "" });
    try {
      const res = await axios.put(`${apiUrl}api/users/${editing._id}`, payload);
      // Defensive log so admin can see exactly what came back. If the
      // table reverts on refresh, this is the FIRST thing to check.
      // eslint-disable-next-line no-console
      console.log("[UsersByType] PUT response:", res.status, res.data);

      // customerComments goes through its own dedicated route: the PUT
      // route's field whitelist/schema strips undeclared fields, so we
      // save the comments via updateCustomerComments (native-driver
      // update, proven to persist) after the main save succeeds.
      if (typeof this.state.editDraft.customerComments === "string") {
        try {
          await axios.post(`${apiUrl}api/users/updateCustomerComments`, {
            name:
              this.state.editDraft.name ||
              this.state.editDraft.customerId ||
              editing.name ||
              editing.customerId,
            comments: this.state.editDraft.customerComments,
          });
        } catch (ccErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[UsersByType] comments save failed:",
            ccErr.response && ccErr.response.status,
            ccErr.response && ccErr.response.data
          );
        }
      }

      // Close the modal, then do a fresh fetch from the backend. This
      // guarantees the row in the table reflects what's actually in the
      // DB — not just what the API claimed to return. If the row reverts,
      // the write didn't persist (wrong DB / stale route / permissions).
      this.setState(
        { saving: false, editing: null, editDraft: {} },
        () => this.load()
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[UsersByType] PUT failed:", err.response && err.response.status, err.response && err.response.data);
      this.setState({
        saving: false,
        saveError:
          (err.response && err.response.data && err.response.data.error) ||
          err.message ||
          "Save failed",
      });
    }
  };

  renderTypeChip(type) {
    const { activeType, typeCounts, typeLabels } = this.state;
    const label = typeLabels[type] || TYPE_LABELS_FALLBACK[type] || `Type ${type}`;
    const count = typeCounts[String(type)] || 0;
    const isActive = activeType === type;
    return (
      <button
        key={type}
        className={"ubt-chip" + (isActive ? " ubt-chip--active" : "")}
        onClick={() => this.pickType(type)}
        type="button"
      >
        <span className="ubt-chip__label">{label}</span>
        <span className="ubt-chip__count">{count}</span>
      </button>
    );
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div className="ubt-root">
          <SideBar
            menus={[
              { title: "Patient List",       url: "/patient-list" },
              { title: "Verifier Home",      url: "/verifier-home" },
              { title: "Analysis Dashboard", url: "/analysis-dashboard" },
              { title: "Users by Role",      url: "/users-by-role" },
              { title: "Telemetry",          url: "/telemetry" },
              { title: "Assign Patients",    url: "/assign-patients" },
              { title: "Verified",           url: "/Verification-Completed" },
              { title: "QC",                 url: "/qc_home" },
            ]}
            profileLink="verifier-profile"
          />
          <div className="ubt-page">
            <div className="ubt-error">{error}</div>
          </div>
        </div>
      );
    }

    const {
      activeType, qInput, onlyActive,
      users, total, offset, limit, loading, typeCounts,
    } = this.state;

    const totalAll = Object.values(typeCounts).reduce((s, n) => s + n, 0);

    return (
      <div className="ubt-root">
        <SideBar
          menus={[
            { title: "Patient List",       url: "/patient-list" },
            { title: "Verifier Home",      url: "/verifier-home" },
            { title: "Analysis Dashboard", url: "/analysis-dashboard" },
            { title: "Users by Role",      url: "/users-by-role" },
            { title: "Telemetry",          url: "/telemetry" },
            { title: "Assign Patients",    url: "/assign-patients" },
            { title: "Verified",           url: "/Verification-Completed" },
            { title: "QC",                 url: "/qc_home" },
          ]}
          profileLink="verifier-profile"
        />

        <div className="ubt-page">
          <div className="ubt-header">
            <div>
              <h2>Users by role</h2>
              <span className="ubt-sub">
                {totalAll.toLocaleString()} users · grouped by their type field
              </span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="ubt-btn"
                onClick={this.handleExportUsers}
                disabled={this.state.exportBusy}
                title="Download current users as Excel (fetches full details per user — may take a few seconds)"
              >
                {this.state.exportBusy ? "Exporting…" : "⬇ Export"}
              </button>
              <button
                type="button"
                className="ubt-btn"
                onClick={this.handleImportClick}
                disabled={this.state.importBusy}
                title="Upload Excel to bulk-update users (matched by _id)"
              >
                {this.state.importBusy ? "Importing…" : "⬆ Import"}
              </button>
              <input
                ref={this.fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={this.handleImportFile}
              />
              <button
                type="button"
                className="ubt-btn"
                onClick={this.openStats}
                title="Patient counts per customer over a date range"
              >
                📊 Customer stats
              </button>
            </div>
          </div>

          {/* Type chips */}
          <div className="ubt-chips">
            <button
              className={"ubt-chip" + (activeType == null ? " ubt-chip--active" : "")}
              onClick={() => this.pickType(null)}
              type="button"
            >
              <span className="ubt-chip__label">All</span>
              <span className="ubt-chip__count">{totalAll}</span>
            </button>
            {TYPE_ORDER.map((t) => this.renderTypeChip(t))}
          </div>

          {/* Sub-chips for Customer/Partner only — Partner / Hospital / Distributor */}
          {activeType === 7 && (
            <div className="ubt-subchips">
              <span className="ubt-subchips__label">Within Customer / Partner:</span>
              <button
                className={"ubt-subchip" + (this.state.activeCategory == null ? " ubt-subchip--active" : "")}
                onClick={() => this.pickCategory(null)}
                type="button"
              >
                All
              </button>
              {CATEGORY_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  className={"ubt-subchip" + (this.state.activeCategory === c.value ? " ubt-subchip--active" : "")}
                  onClick={() => this.pickCategory(c.value)}
                  type="button"
                >
                  {c.value === "hospital" ? "🏥 " : c.value === "distributor" ? "📦 " : ""}{c.label}
                </button>
              ))}
            </div>
          )}

          {/* Search + filters */}
          <div className="ubt-filters">
            <div className="ubt-search-wrap">
              <input
                className="ubt-search"
                type="text"
                placeholder="Search name, email, phone, or customer ID…"
                value={qInput}
                onChange={(e) => this.onSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && this.applySearch()}
              />
              {qInput && (
                <button
                  type="button"
                  className="ubt-search__clear"
                  onClick={this.clearSearch}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            {loading && <span className="ubt-loading-tag">Searching…</span>}
            <label className="ubt-check">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={this.toggleActiveOnly}
              />
              Active users only
            </label>
          </div>

          {/* Table */}
          <div className="ubt-table-wrap">
            <table className="ubt-table">
              <thead>
                <tr>
                  <th>Customer ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Account Coordinator</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" className="ubt-empty">
                      No users match these filters.
                    </td>
                  </tr>
                )}
                {users.map((u) => {
                  const hasContact = u.contactPerson || u.whatsappNumber || u.mailContact;
                  return (
                    <tr key={u._id}>
                      <td className="ubt-cust-cell">
                        {u.customerId ? (
                          <>
                            <div className="ubt-cust">{u.customerId}</div>
                            {u.type === 7 && u.accountCategory && u.accountCategory !== "partner" && (
                              <div className="ubt-cat">
                                <span className={"ubt-cat__chip ubt-cat__chip--" + u.accountCategory}>
                                  {u.accountCategory === "hospital" ? "🏥" : "📦"} {u.accountCategory}
                                </span>
                                {u.parentCustomerId && (
                                  <div className="ubt-cat__parent">
                                    <span className="ubt-cat__parent-arrow">↳</span>{" "}
                                    {u.parentCustomerName ? (
                                      <>
                                        <span className="ubt-cat__parent-name">{u.parentCustomerName}</span>{" "}
                                        <span className="ubt-cust">{u.parentCustomerId}</span>
                                      </>
                                    ) : (
                                      <span className="ubt-cust">{u.parentCustomerId}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="ubt-empty-cell">—</span>
                        )}
                      </td>
                      <td className="ubt-name">{u.name || "—"}</td>
                      <td>{u.email || "—"}</td>
                      <td>{u.phone || "—"}</td>
                      <td className="ubt-contact">
                        {hasContact ? (
                          <>
                            {u.contactPerson && (
                              <div className="ubt-contact__name">{u.contactPerson}</div>
                            )}
                            {u.whatsappNumber && (
                              <div className="ubt-contact__line">📱 {u.whatsappNumber}</div>
                            )}
                            {u.mailContact && (
                              <div className="ubt-contact__line">✉ {u.mailContact}</div>
                            )}
                          </>
                        ) : (
                          <span className="ubt-empty-cell">—</span>
                        )}
                      </td>
                      <td className="ubt-status-cell">
                        <span className={u.isActive ? "ubt-pill ubt-pill--good" : "ubt-pill ubt-pill--bad"}>
                          {u.isActive ? "Active" : "Disabled"}
                        </span>
                        <button
                          className="ubt-btn ubt-btn--sm"
                          onClick={() => this.openEdit(u)}
                          title="Edit this user"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="ubt-paging">
              <button
                className="ubt-btn ubt-btn--ghost"
                onClick={() => this.changePage(-1)}
                disabled={offset === 0 || loading}
              >
                ◀ Prev
              </button>
              <span className="ubt-paging__info">
                {offset + 1}–{Math.min(offset + limit, total)} of {total.toLocaleString()}
              </span>
              <button
                className="ubt-btn ubt-btn--ghost"
                onClick={() => this.changePage(1)}
                disabled={offset + limit >= total || loading}
              >
                Next ▶
              </button>
            </div>
          )}

          {/* Stats modal — per-customer patient counts over a date range */}
          {this.state.statsOpen && (
            <div className="ubt-modal-backdrop" onClick={this.closeStats}>
              <div
                className="ubt-modal ubt-modal--wide"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="ubt-modal__head">
                  <div>
                    <h3>Customer statistics</h3>
                    <span className="ubt-modal__sub">
                      Patient counts per customer · {this.resolveStatsRange().from} → {this.resolveStatsRange().to}
                    </span>
                  </div>
                  <button className="ubt-modal__x" onClick={this.closeStats} aria-label="Close">✕</button>
                </div>

                <div className="ubt-stats__filters">
                  {["today", "week", "month", "custom"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={"ubt-subchip" + (this.state.statsPeriod === p ? " ubt-subchip--active" : "")}
                      onClick={() => this.setStatsPeriod(p)}
                    >
                      {p === "today" ? "Today" :
                       p === "week"  ? "Last 7 days" :
                       p === "month" ? "Last 30 days" : "Custom"}
                    </button>
                  ))}
                  {this.state.statsPeriod === "custom" && (
                    <>
                      <input
                        type="date"
                        className="ubt-field__input"
                        value={this.state.statsFrom}
                        onChange={(e) => this.setState({ statsFrom: e.target.value })}
                      />
                      <span style={{ color: "#94a3b8" }}>→</span>
                      <input
                        type="date"
                        className="ubt-field__input"
                        value={this.state.statsTo}
                        onChange={(e) => this.setState({ statsTo: e.target.value })}
                      />
                      <button type="button" className="ubt-btn ubt-btn--sm" onClick={this.loadStats}>
                        Load
                      </button>
                    </>
                  )}
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    className="ubt-btn ubt-btn--ghost ubt-btn--sm"
                    onClick={this.exportStatsCsv}
                    disabled={!this.state.statsRows.length}
                  >
                    ⬇ Export CSV
                  </button>
                </div>

                {this.state.statsError && (
                  <div className="ubt-modal__error">{this.state.statsError}</div>
                )}

                <div className="ubt-stats__table-wrap">
                  {this.state.statsLoading ? (
                    <div className="ubt-empty">Loading stats…</div>
                  ) : (
                    <table className="ubt-table">
                      <thead>
                        <tr>
                          <th>Customer ID</th>
                          <th>Customer</th>
                          <th title="Patients registered (created_at) in this period">Registered</th>
                          <th title="Of those, the ones whose data was uploaded (data_count > 0)">Uploaded</th>
                          <th title="Of those, the ones with a generated report (ReportGeneration set)">Report generated</th>
                          <th title="Sum of expected monitoring time scheduled (exp_device_use_days × 24). NOT actual ECG hours captured — it's what the devices were booked for.">Expected device-hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.statsRows.length === 0 && (
                          <tr><td colSpan="6" className="ubt-empty">No customers with patients in this range.</td></tr>
                        )}
                        {this.state.statsRows.map((r) => (
                          <tr
                            key={r.customerId}
                            onClick={() => r.registered && this.exportStatsCsv(r.customerId)}
                            style={{ cursor: r.registered ? "pointer" : "default" }}
                            title={r.registered
                              ? `Click any cell to download ${r.customerName} (${r.customerId}) patient detail CSV`
                              : "No patients to export in this period"}
                          >
                            <td className="ubt-cust" style={{ textDecoration: r.registered ? "underline" : "none" }}>
                              {r.customerId} {r.registered ? "⬇" : ""}
                            </td>
                            <td>
                              {r.customerName}
                              {r.accountCategory === "hospital" && (
                                <span className="ubt-cat__chip ubt-cat__chip--hospital" style={{ marginLeft: 8 }}>🏥 hospital</span>
                              )}
                              {r.accountCategory === "distributor" && (
                                <span className="ubt-cat__chip ubt-cat__chip--distributor" style={{ marginLeft: 8 }}>📦 distributor</span>
                              )}
                            </td>
                            <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{r.registered}</td>
                            <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{r.uploaded}</td>
                            <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{r.reportGenerated}</td>
                            <td style={{ textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{r.totalHours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="ubt-modal__actions">
                  <button type="button" className="ubt-btn ubt-btn--ghost" onClick={this.closeStats}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit modal */}
          {this.state.editing && (
            <div className="ubt-modal-backdrop" onClick={this.closeEdit}>
              <div className="ubt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ubt-modal__head">
                  <div>
                    <h3>Edit user</h3>
                    <span className="ubt-modal__sub">
                      {this.state.editing.typeLabel}
                      {this.state.editing.customerId && (
                        <> · <span className="ubt-cust">{this.state.editing.customerId}</span></>
                      )}
                    </span>
                  </div>
                  <button className="ubt-modal__x" onClick={this.closeEdit} aria-label="Close">
                    ✕
                  </button>
                </div>

                <div className="ubt-modal__body">
                  {/* Role / level — top of the form because it has the
                      biggest blast radius. Backend whitelists `type`. */}
                  <label className="ubt-field ubt-field--wide">
                    <span className="ubt-field__label">Role / level</span>
                    <select
                      className="ubt-field__input ubt-field__select"
                      value={
                        this.state.editDraft.type !== undefined
                          ? String(this.state.editDraft.type)
                          : ""
                      }
                      onChange={(e) => this.setDraft("type", parseInt(e.target.value, 10))}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={String(r.value)}>
                          {r.label} (type {r.value})
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const sel = ROLE_OPTIONS.find(
                        (r) => r.value === Number(this.state.editDraft.type)
                      );
                      return sel ? (
                        <span className="ubt-field__hint">{sel.hint}</span>
                      ) : null;
                    })()}
                  </label>

                  {/* Sub-category (Partner / Hospital / Distributor) — only
                      meaningful for type-7 users. Show always so admin can
                      promote a misclassified user, but the "parent partner"
                      picker only appears when category === hospital. */}
                  {Number(this.state.editDraft.type) === 7 && (
                    <>
                      <label className="ubt-field ubt-field--wide">
                        <span className="ubt-field__label">Account category</span>
                        <select
                          className="ubt-field__input ubt-field__select"
                          value={this.state.editDraft.accountCategory || "partner"}
                          onChange={(e) => this.setDraft("accountCategory", e.target.value)}
                        >
                          {CATEGORY_OPTIONS.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                        {(() => {
                          const sel = CATEGORY_OPTIONS.find(
                            (c) => c.value === (this.state.editDraft.accountCategory || "partner")
                          );
                          return sel ? (
                            <span className="ubt-field__hint">{sel.hint}</span>
                          ) : null;
                        })()}
                      </label>

                      {this.state.editDraft.accountCategory === "hospital" && (
                        <label className="ubt-field ubt-field--wide">
                          <span className="ubt-field__label">Parent partner</span>
                          <select
                            className="ubt-field__input ubt-field__select"
                            value={this.state.editDraft.parentCustomerId || ""}
                            onChange={(e) => this.setDraft("parentCustomerId", e.target.value)}
                          >
                            <option value="">— Standalone hospital (no parent) —</option>
                            {this.state.partnersForParent.map((p) => (
                              <option key={p._id} value={p.customerId}>
                                {p.customerId} · {p.name}
                              </option>
                            ))}
                          </select>
                          <span className="ubt-field__hint">
                            Roll this hospital's devices, patients and invoices up under the selected partner.
                          </span>
                        </label>
                      )}
                    </>
                  )}

                  {/* Hospital → database_id sanity warning. Surfaces the
                      "all doctors are listed under this hospital" bug
                      caused by a hospital row missing its database_id
                      link to a specific doctor record. */}
                  {Number(this.state.editDraft.type) === 6
                    && !String(this.state.editDraft.database_id || "").trim() && (
                    <div className="ubt-warn"
                         style={{
                           gridColumn: "1 / -1",
                           background: "rgba(245,158,11,0.10)",
                           border: "1px solid rgba(245,158,11,0.45)",
                           color: "#fbbf24",
                           borderRadius: 8,
                           padding: "10px 14px",
                           fontSize: 12.5,
                           lineHeight: 1.5,
                           margin: "4px 0 8px",
                         }}>
                      ⚠ This Hospital is missing <b>Linked DB ID</b>
                      (<code>database_id</code>). Without it, the
                      doctor-listing query falls back to "match all", so
                      every doctor on the system shows under this
                      hospital. Paste the matching doctor record's
                      ObjectId into the field below to fix it.
                    </div>
                  )}

                  {EDITABLE_FIELDS.map((f) => (
                    <label key={f.key} className="ubt-field">
                      <span className="ubt-field__label">{f.label}</span>
                      {f.multiline ? (
                        <textarea
                          rows={3}
                          className={"ubt-field__input" + (f.mono ? " ubt-field__input--mono" : "")}
                          style={{ resize: "vertical", fontFamily: "inherit" }}
                          placeholder={f.placeholder}
                          value={this.state.editDraft[f.key] || ""}
                          onChange={(e) => this.setDraft(f.key, e.target.value)}
                        />
                      ) : (
                        <input
                          type={f.type || "text"}
                          className={"ubt-field__input" + (f.mono ? " ubt-field__input--mono" : "")}
                          placeholder={f.placeholder}
                          value={this.state.editDraft[f.key] || ""}
                          onChange={(e) => this.setDraft(f.key, e.target.value)}
                        />
                      )}
                    </label>
                  ))}

                  <label className="ubt-field ubt-field--row">
                    <input
                      type="checkbox"
                      checked={!!this.state.editDraft.isActive}
                      onChange={(e) => this.setDraft("isActive", e.target.checked)}
                    />
                    <span>Account is active</span>
                  </label>
                </div>

                {this.state.saveError && (
                  <div className="ubt-modal__error">{this.state.saveError}</div>
                )}

                <div className="ubt-modal__actions">
                  <button
                    type="button"
                    className="ubt-btn ubt-btn--ghost"
                    onClick={this.closeEdit}
                    disabled={this.state.saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ubt-btn"
                    onClick={this.saveEdit}
                    disabled={this.state.saving}
                  >
                    {this.state.saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
